import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, tap } from 'rxjs';
import { exhaustMap, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { IApprovalState } from './approval.state';
import { withApprovalStateMethods } from './approval.methods.state';
import { ApprovalPolicyService } from '../../services/approval/approval-policy.service';
import { NotificationService } from '../../services/notification/notification.service';
import {
    CreateApprovalPolicy,
    EditApprovalPolicy,
    AssignDepartmentPolicy,
} from '../../models/approval/approval-policy.model';

export const withApprovalApiMethods = signalStoreFeature(
    { state: type<IApprovalState>() },
    withApprovalStateMethods,
    withMethods((store) => {
        const service = inject(ApprovalPolicyService);
        const notificationService = inject(NotificationService);

        return {
            // ── Policies ──────────────────────────────────────────────────────

            loadPolicies: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(() =>
                        service.getPolicies().pipe(
                            tapResponse({
                                next: (res) => store.setPolicies(res.policies),
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            createPolicy: rxMethod<{ payload: CreateApprovalPolicy; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(({ payload, onSuccess }) =>
                        service.createPolicy(payload).pipe(
                            tapResponse({
                                next: (res) => {
                                    store.addPolicy(res.policy);
                                    notificationService.notify('success', 'Policy Created', `"${res.policy.policyName}" has been created.`);
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            editPolicy: rxMethod<{ policyGuid: string; payload: EditApprovalPolicy; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    switchMap(({ policyGuid, payload, onSuccess }) =>
                        service.editPolicy(policyGuid, payload).pipe(
                            tapResponse({
                                next: (res) => {
                                    store.updatePolicy(res.policy);
                                    notificationService.notify('success', 'Policy Updated', `"${res.policy.policyName}" has been updated.`);
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            deletePolicy: rxMethod<{ policyGuid: string; onSuccess?: () => void }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    switchMap(({ policyGuid, onSuccess }) =>
                        service.deletePolicy(policyGuid).pipe(
                            tapResponse({
                                next: () => {
                                    store.removePolicy(policyGuid);
                                    notificationService.notify('success', 'Policy Deleted', 'The approval policy has been removed.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            // ── Department Assignments ─────────────────────────────────────────

            loadDepartmentPolicies: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    exhaustMap(() =>
                        service.getDepartmentPolicies().pipe(
                            tapResponse({
                                next: (res) => store.setDepartmentPolicies(res.items),
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),

            assignDepartmentPolicy: rxMethod<{
                payload: AssignDepartmentPolicy;
                policyName: string | null;
                onSuccess?: () => void;
            }>(
                pipe(
                    tap(() => patchState(store, { loading: true, error: null })),
                    switchMap(({ payload, policyName, onSuccess }) =>
                        service.assignDepartmentPolicy(payload).pipe(
                            tapResponse({
                                next: () => {
                                    store.updateDepartmentPolicyEntry(
                                        payload.departmentGuid,
                                        payload.requestType,
                                        payload.policyGuid,
                                        policyName,
                                    );
                                    notificationService.notify('success', 'Policy Assigned', 'Department approval policy updated.');
                                    onSuccess?.();
                                },
                                error: (err: HttpErrorResponse) => {
                                    const msg = err.error?.message ?? err.message;
                                    store.setError(msg);
                                    notificationService.notifyError('Error', msg);
                                },
                            })
                        )
                    )
                )
            ),
        };
    })
);
